import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { proposals, apiErrorMessage, type ProposalView } from '../lib/api';
import { useApi } from '../hooks/useApi';
import { useTranslation, type TKey } from '../i18n';
import { Sidenote, SidenoteLayout } from '../components/ui';

const LABELS = ['A', 'B', 'C', 'D'];

const STATUS_KEY: Record<ProposalView['status'], TKey> = {
  pending: 'prop.status.pending',
  accepted: 'prop.status.accepted',
  rejected: 'prop.status.rejected',
};

export default function ProposePage() {
  const { t } = useTranslation();
  const { bookId = '' } = useParams();

  const [stem, setStem] = useState('');
  const [texts, setTexts] = useState<string[]>(['', '', '', '']);
  const [correct, setCorrect] = useState('A');
  const [tier, setTier] = useState('');
  const [explanation, setExplanation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const mine = useApi(() => proposals.mine(bookId), [bookId, refreshKey]);
  const myList = mine.status === 'success' ? mine.data : [];

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stem.trim()) { toast.error(t('prop.needStem')); return; }
    const filled = texts.filter((x) => x.trim()).length;
    if (filled < 2) { toast.error(t('prop.needChoices')); return; }
    if (!texts[LABELS.indexOf(correct)]?.trim()) { toast.error(t('prop.needCorrect')); return; }

    const choices = texts
      .map((text, i) => ({ label: LABELS[i], text: text.trim() }))
      .filter((c) => c.text);

    setSubmitting(true);
    try {
      await proposals.submit({
        book_id: bookId,
        stem: stem.trim(),
        choices,
        correct_choice: correct,
        tier: tier || null,
        explanation: explanation.trim() || null,
      });
      toast.success(t('prop.success'));
      setStem(''); setTexts(['', '', '', '']); setCorrect('A'); setTier(''); setExplanation('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="container propose-page">
      <SidenoteLayout>
        <SidenoteLayout.Main>
          <div className="prop-head">
            <Link to={`/books/${bookId}`} className="prop-back">← {t('common.back')}</Link>
            <h1>{t('prop.title')}</h1>
            <p className="prop-help">{t('prop.help')}</p>
          </div>

          <form onSubmit={onSubmit} className="prop-form">
            <label className="prop-field">
              <span className="prop-label">{t('prop.stemLabel')}</span>
              <textarea
                className="prop-textarea"
                value={stem}
                onChange={(e) => setStem(e.target.value)}
                placeholder={t('prop.stemPlaceholder')}
                rows={3}
              />
            </label>

            <fieldset className="prop-choices">
              <legend className="prop-label">{t('prop.choicesLabel')}</legend>
              {LABELS.map((label, i) => (
                <div key={label} className={`prop-choice${correct === label ? ' is-correct' : ''}`}>
                  <input
                    type="radio"
                    name="correct"
                    id={`correct-${label}`}
                    checked={correct === label}
                    onChange={() => setCorrect(label)}
                    aria-label={`${t('prop.choiceCorrect')} ${label}`}
                  />
                  <span className="prop-choice__label" aria-hidden="true">{label}</span>
                  <input
                    type="text"
                    className="prop-choice__text"
                    value={texts[i]}
                    onChange={(e) =>
                      setTexts((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))
                    }
                    placeholder={t('prop.choicePlaceholder')}
                  />
                </div>
              ))}
            </fieldset>

            <label className="prop-field">
              <span className="prop-label">{t('prop.difficulty')}</span>
              <select className="prop-choice__text" value={tier} onChange={(e) => setTier(e.target.value)}>
                <option value="">{t('prop.difficultyNone')}</option>
                {['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9'].map((tv) => (
                  <option key={tv} value={tv}>{tv}</option>
                ))}
              </select>
            </label>

            <label className="prop-field">
              <span className="prop-label">{t('prop.explanationLabel')}</span>
              <textarea
                className="prop-textarea"
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                placeholder={t('prop.explanationPlaceholder')}
                rows={2}
              />
            </label>

            <button type="submit" className="btn btn-accent" disabled={submitting}>
              {submitting ? t('prop.submitting') : t('prop.submit')}
            </button>
          </form>

          <section className="prop-mine">
            <div className="sec-head">
              <h2>{t('prop.myProposals')}</h2>
              <span className="meta">{myList.length}</span>
            </div>
            {mine.status === 'success' && myList.length === 0 && (
              <p className="prop-empty">{t('prop.none')}</p>
            )}
            {myList.length > 0 && (
              <ul className="prop-list">
                {myList.map((p) => (
                  <li key={p.id} className="prop-item">
                    <span className="prop-item__stem">{p.stem}</span>
                    <span className={`prop-chip prop-chip--${p.status}`}>
                      {t(STATUS_KEY[p.status])}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </SidenoteLayout.Main>

        <SidenoteLayout.Aside>
          <Sidenote label={t('prop.aside.howLabel')} marker="?">
            <p>{t('prop.aside.howBody')}</p>
          </Sidenote>
          <Sidenote label={t('prop.aside.rewardLabel')} tone="accent">
            <p>{t('prop.aside.rewardBody')}</p>
          </Sidenote>
        </SidenoteLayout.Aside>
      </SidenoteLayout>
    </main>
  );
}
